import { useState } from "react";
import { TableRow, TableRowStatus } from "../../../../components/tables";
import { getTime, getStatusColor, getLicensorName } from "../helpers";
import {
  api,
  publishLicenseTask,
  restartLicenseTask,
} from "../../../../services/axios";
import { LicenseType } from "../../../../utils/types";
import { toast } from "sonner";
import { useHistory } from "react-router-dom";

export function LicensingTable({
  licensing,
  page,
  previousPage,
  nextPage,
}: {
  licensing: LicenseType[];
  page: number;
  previousPage: () => void;
  nextPage: () => void;
}) {
  let history = useHistory();
  const [selected, setSelected] = useState<string[]>([]);

  const publishLicense = (licenseId: string) => {
    return publishLicenseTask(licenseId)
      .then(() => {
        toast.success("started", { duration: 5000 });
      })
      .catch((e) => {
        toast.error(e.message, { duration: 5000 });
      });
  };

  const restartLicense = (licenseId: string) => {
    return restartLicenseTask(licenseId)
      .then(() => {
        toast.success("started", { duration: 5000 });
      })
      .catch((e) =>
        restartLicenseTask(licenseId).then(() => {
          toast.error(e.message, { duration: 5000 });
        })
      );
  };

  const deleteThisLicense = async (licenseId: string) => {
    return api
      .delete(`licensing/${licenseId}`)
      .then(() => toast.success("deleted", { duration: 5000 }))
      .catch((e) => toast.error(e.message, { duration: 5000 }));
  };

  function handleSelect(id: string) {
    if (selected.includes(id)) {
      const removed = selected.filter((x) => x !== id);
      setSelected(removed);
    } else {
      setSelected([...selected, id]);
    }
  }

  async function deleteAllSelectedLicensing() {
    for (let i = 0; i < selected.length; i++) {
      await deleteThisLicense(selected[i]);
      toast.success(selected[i], { duration: 5000 });
    }
    setSelected([]);
    toast.success("deleted", { duration: 5000 });
  }

  function viewLicense(licenseId: string) {
    history.push({
      pathname: `/license/${licenseId}`,
    });
  }

  async function publishSelectedLicensing() {
    for (let i = 0; i < selected.length; i++) {
      await publishLicense(selected[i])
        .then(() => toast.success(`${selected[i]} posted`, { duration: 5000 }))
        .catch((e) => toast.error(`${selected[i]} failed`, { duration: 5000 }));
    }
  }

  return (
    <div className="w-full p-8 m-4 border rounded">
      <div className="flex-1 min-w-0 w-full">
        <h4 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
          License Request History
        </h4>
        <p className="italic text-xs">Limited to the last month of requests</p>
      </div>
      <div className="w-full flex justify-between items-center gap-4">
        <div className="space-x-4">
          <button
            onClick={() => setSelected(licensing.map((x) => x.id))}
            className="bg-white border px-4 py-2 text-black my-4"
          >
            select all
          </button>
          <button
            onClick={() => setSelected([])}
            className="bg-white border px-4 py-2 text-black my-4"
          >
            de-select all
          </button>
        </div>
        <button
          onClick={() => deleteAllSelectedLicensing()}
          className="bg-black px-4 py-2 text-white my-4"
        >
          delete selected
        </button>
      </div>
      <div>
        <table className="table-fixed w-full border-collapse border border-deeppurple text-xs">
          <thead>
            <tr>
              <th className="border border-deeppurple">select</th>
              <th className="border border-deeppurple">license id</th>
              <th className="border border-deeppurple">timestamp</th>
              <th className="border border-deeppurple">organization</th>
              <th className="border border-deeppurple">status</th>
              <th className="border border-deeppurple">licensor</th>
              <th className="border border-deeppurple"></th>
              <th className="border border-deeppurple"></th>
              <th className="border border-deeppurple"></th>
            </tr>
          </thead>
          <tbody>
            {licensing.map((license, index) => {
              return (
                <tr className="p-2 m-2 group" key={index}>
                  <td className="p-2 border border-deeppurple truncate group-hover:bg-gray-50">
                    <div className="w-full h-full flex justify-center items-center">
                      <input
                        id={`${license.id}`}
                        name={`${license.id}`}
                        type="checkbox"
                        checked={selected.includes(`${license.id}`)}
                        className="focus:ring-dustyrose h-4 w-4 text-dustyrose border-gray-300 rounded"
                        onChange={() => handleSelect(`${license.id}`)}
                      />
                    </div>
                  </td>
                  <td className="p-2 border border-deeppurple truncate group-hover:bg-gray-50">
                    {license.id}
                  </td>
                  <TableRow
                    tooltip={true}
                    value={getTime(license.created_at)}
                  />
                  <TableRow
                    tooltip={true}
                    value={
                      license.organization_name ? license.organization_name : ""
                    }
                  />
                  <TableRowStatus
                    tooltip={true}
                    statusColor={getStatusColor(license)}
                    value={license.status}
                  />
                  <TableRow
                    tooltip={true}
                    value={getLicensorName(license.licensor)}
                  />
                  {license.status === "review" ? (
                    <td className="p-2 border border-deeppurple truncate group-hover:bg-gray-200 cursor-pointer underline">
                      <button
                        className="underline"
                        onClick={() => publishLicense(license.id)}
                      >
                        Publish
                      </button>
                    </td>
                  ) : (
                    <td className="p-2 border border-deeppurple truncate group-hover:bg-gray-200 cursor-pointer underline">
                      <button
                        onClick={() => restartLicense(license.id)}
                        className="underline"
                      >
                        Restart
                      </button>
                    </td>
                  )}
                  <td className="p-2 border border-deeppurple truncate group-hover:bg-gray-200 cursor-pointer underline">
                    <button
                      onClick={() => viewLicense(license.id)}
                      className="underline"
                    >
                      View
                    </button>
                  </td>
                  {license.licensor === "affinity" ? (
                    <td className="p-2 border border-deeppurple truncate group-hover:bg-gray-50">
                      {license.affinity_license_id}
                    </td>
                  ) : (
                    <td className="p-2 border border-deeppurple truncate group-hover:bg-gray-50">
                      {license.license_files?.map(function (
                        file: string,
                        index: number
                      ) {
                        return (
                          <div key={index}>
                            <a href={file} download>
                              <p className="underline">Download</p>
                            </a>
                          </div>
                        );
                      })}
                    </td>
                  )}
                  <td className="p-2 border border-deeppurple truncate group-hover:bg-gray-50">
                    <button
                      className="underline"
                      onClick={() => deleteThisLicense(license.id)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <nav
        className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6 mt-6"
        aria-label="Pagination"
      >
        <div className="hidden sm:block">
          <p className="text-sm text-gray-700">
            Showing page <span className="font-medium">{`${page}`}</span>
          </p>
        </div>
        <div className="flex flex-1 justify-between sm:justify-end">
          <button
            onClick={() => previousPage()}
            className="relative ml-3 inline-flex items-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus-visible:outline-offset-0"
          >
            Previous
          </button>
          <button
            onClick={() => nextPage()}
            className="relative ml-3 inline-flex items-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus-visible:outline-offset-0"
          >
            Next
          </button>
        </div>
      </nav>
      <div className="w-full flex justify-start">
        <div className="my-2 ml-2">
          <button
            onClick={() => publishSelectedLicensing()}
            className="bg-black px-4 py-2 text-white my-4"
          >
            Publish Selected Licensing
          </button>
        </div>
      </div>
    </div>
  );
}
